import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, API_BASE_URL, ApiError, type ApiKey, type CreatedApiKey } from "../lib/api";
import { copyToClipboard, formatRelative } from "../lib/format";
import { useToast } from "./Toast";

/**
 * Keys for the machine-facing endpoint. This is the panel an operator uses to
 * wire a blog build or an editor plugin into the uploader.
 */
export function ApiKeysPanel() {
  const { notify } = useToast();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [name, setName] = useState("");
  const [freshKey, setFreshKey] = useState<CreatedApiKey | null>(null);
  const [isWorking, setIsWorking] = useState(false);

  const load = useCallback(async () => {
    try {
      const { items } = await api.apiKeys.list();
      setKeys(items);
    } catch (cause) {
      if (cause instanceof ApiError && cause.isAuthFailure) return;
      notify("Could not load the API keys.", "error");
    }
  }, [notify]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    setIsWorking(true);
    try {
      const created = await api.apiKeys.create(name);
      setFreshKey(created);
      setName("");
      await load();
    } catch (cause) {
      notify(cause instanceof ApiError ? cause.message : "Could not create the key.", "error");
    } finally {
      setIsWorking(false);
    }
  }

  async function handleRevoke(key: ApiKey) {
    if (
      !window.confirm(
        `Revoke "${key.name}"?\n\nAnything using it stops uploading immediately. ` +
          `The key stays listed so you can see it existed. This cannot be undone.`,
      )
    ) {
      return;
    }
    try {
      await api.apiKeys.revoke(key.id);
      notify(`Revoked "${key.name}".`, "info");
      await load();
    } catch (cause) {
      notify(cause instanceof ApiError ? cause.message : "Could not revoke the key.", "error");
    }
  }

  async function handleDelete(key: ApiKey) {
    const stillLive = key.revoked_at === null;
    if (
      !window.confirm(
        `Delete "${key.name}"?\n\n` +
          (stillLive ? "It is still active, so anything using it stops working immediately.\n\n" : "") +
          `This removes the key and its history for good.`,
      )
    ) {
      return;
    }
    try {
      await api.apiKeys.remove(key.id);
      notify(`Deleted "${key.name}".`, "info");
      await load();
    } catch (cause) {
      notify(cause instanceof ApiError ? cause.message : "Could not delete the key.", "error");
    }
  }

  return (
    <section className="panel">
      <header className="panel__head">
        <span className="label">Upload keys</span>
      </header>

      <div className="panel__body">
        {freshKey && (
          <div className="secret">
            <span className="label">Copy this now, it is not shown again</span>
            <code>{freshKey.secret}</code>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button
                className="btn"
                type="button"
                onClick={async () => {
                  const copied = await copyToClipboard(freshKey.secret);
                  notify(copied ? "Key copied." : "Could not reach the clipboard.", copied ? "success" : "error");
                }}
              >
                Copy key
              </button>
              <button className="btn btn--quiet" type="button" onClick={() => setFreshKey(null)}>
                Done
              </button>
            </div>
          </div>
        )}

        <form className="field" onSubmit={handleCreate}>
          <label className="label" htmlFor="key-name">
            New key
          </label>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <input
              id="key-name"
              type="text"
              placeholder="blog build script"
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
            <button className="btn" type="submit" disabled={isWorking}>
              Create
            </button>
          </div>
        </form>

        {keys.length === 0 ? (
          <p className="field__hint">No keys yet. Create one to use the upload API.</p>
        ) : (
          <ul className="keylist">
            {keys.map((key) => (
              <li className="keylist__item" key={key.id} data-revoked={key.revoked_at !== null}>
                <div className="keylist__body">
                  <div className="keylist__name">
                    {key.name}
                    {key.revoked_at && <span className="keylist__badge">Revoked</span>}
                  </div>
                  <div className="keylist__meta">
                    {key.key_prefix}… ·{" "}
                    {key.revoked_at
                      ? `revoked ${formatRelative(key.revoked_at)}`
                      : `used ${formatRelative(key.last_used_at)}`}
                  </div>
                </div>
                <div className="keylist__actions">
                  {!key.revoked_at && (
                    <button
                      className="btn btn--quiet"
                      type="button"
                      onClick={() => handleRevoke(key)}
                      title="Stop the key working but keep it listed"
                    >
                      Revoke
                    </button>
                  )}
                  <button
                    className="btn btn--quiet btn--danger"
                    type="button"
                    onClick={() => handleDelete(key)}
                    title="Remove the key and its history for good"
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <details>
          <summary className="label" style={{ cursor: "pointer" }}>
            How to use a key
          </summary>
          <pre
            className="mono"
            style={{
              marginTop: "0.6rem",
              padding: "0.6rem",
              background: "var(--emulsion)",
              borderRadius: "var(--radius)",
              overflowX: "auto",
              color: "var(--graphite)",
            }}
          >
{`curl -X POST ${API_BASE_URL}/api/v1/upload \\
  -H "Authorization: Bearer <key>" \\
  -F "file=@photo.png"

# -> {"url":"https://…","id":"…","file_name":"photo-1a2b.png",…}`}
          </pre>
        </details>
      </div>
    </section>
  );
}
